import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { asLang, type Lang } from '@vollgas/shared/i18n'
import { setApiLanguage } from '@vollgas/shared/api'
import { de, type Translations } from './translations/de'
import { en } from './translations/en'

export type { Lang }

const STORAGE_KEY = 'vollgas-technical-language'

interface TranslationContextValue {
  lang: Lang
  t: Translations
  setLang: (lang: Lang) => void
}

const TranslationContext = createContext<TranslationContextValue | null>(null)

function detectInitialLang(): Lang {
  if (typeof window === 'undefined') return 'de'
  const stored = asLang(window.localStorage.getItem(STORAGE_KEY))
  if (stored) return stored
  // de-AT, unconditionally. `navigator.language` is deliberately not
  // consulted: a technician on an English-set phone still works a
  // German-speaking shop floor, and the only thing that should switch
  // the workspace is that technician picking a language themselves.
  return 'de'
}

interface ProviderProps {
  children: ReactNode
  /** Override for tests — bypasses localStorage / browser detection. */
  initialLang?: Lang
}

export function TranslationProvider({ children, initialLang }: ProviderProps) {
  const [lang, setLangState] = useState<Lang>(initialLang ?? detectInitialLang)

  const setLang = useCallback((next: Lang) => {
    setLangState(next)
    try {
      window.localStorage.setItem(STORAGE_KEY, next)
    } catch {
      /* localStorage unavailable (private mode) — ignore */
    }
  }, [])

  useEffect(() => {
    document.documentElement.lang = lang
    // The API answers in this language too — server-built error messages are
    // shown verbatim, so without this the screens read one language and the
    // failures another.
    setApiLanguage(lang === 'de' ? 'de-AT' : 'en')
  }, [lang])

  const value = useMemo<TranslationContextValue>(
    () => ({ lang, t: lang === 'de' ? de : en, setLang }),
    [lang, setLang],
  )

  return (
    <TranslationContext.Provider value={value}>
      {children}
    </TranslationContext.Provider>
  )
}

export function useI18n(): TranslationContextValue {
  const ctx = useContext(TranslationContext)
  if (!ctx) throw new Error('useI18n must be used within a TranslationProvider')
  return ctx
}
